/** Great-circle geometry + the "is it ahead of me" filter. Runs client-side only:
 *  the server never learns a user's position. */

const R = 6371008.8; // IUGG mean Earth radius, metres
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

export type Point = { lat: number; lon: number };

/** Distance in metres. */
export function haversine(a: Point, b: Point): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing a -> b, degrees clockwise from true north, [0, 360). */
export function bearing(a: Point, b: Point): number {
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Smallest absolute difference between two bearings, [0, 180]. */
export function angDiff(a: number, b: number): number {
  return Math.abs((((a - b) % 360) + 540) % 360 - 180);
}

/**
 * Closest range at which a hazard is still worth announcing. Deliberately small:
 * the secondary "tani" alert fires at 3 s time-to-hazard, which is only ~92 m at
 * 110 km/h. Hazards the driver has already passed are removed by the bearing cone,
 * not by this floor, so the floor only needs to suppress jitter right at the point.
 */
export const MIN_ALERT_DISTANCE_M = 25;

/** Lookahead distance in metres: 45 s of travel, clamped to [800, 3000]. */
export function lookaheadMetres(speedMps: number): number {
  const raw = (Number.isFinite(speedMps) ? Math.max(0, speedMps) : 0) * 45;
  return Math.min(3000, Math.max(800, raw));
}

export type AheadInput = {
  me: Point;
  /** GPS course over ground in degrees. NEVER the compass — dashboard magnets ruin it. */
  course: number | null;
  speedMps: number;
  hazard: Point;
  /** true for hazards that only matter to traffic travelling the same way */
  directional: boolean;
  /** reporter's course when the hazard was filed, if known */
  reporterCourse: number | null;
};

export type AheadVerdict =
  | { show: true; distance: number }
  | { show: false; distance: number; reason: "too-far" | "too-close" | "behind" | "off-corridor" | "opposite-side" };

/**
 * Decide whether a hazard is ahead of the driver, on their road.
 *
 * The cross-track test is a corridor test, not map-matching: it will false-positive
 * on parallel carriageways and overpasses. Accepted in v0 — that is why "nuk eshte"
 * is a whole-screen swipe. OSM way-snapping replaces this in v2.
 */
export function isAhead(input: AheadInput): AheadVerdict {
  const { me, course, speedMps, hazard, directional, reporterCourse } = input;
  const distance = haversine(me, hazard);
  const lookahead = lookaheadMetres(speedMps);

  if (distance > lookahead) return { show: false, distance, reason: "too-far" };
  if (distance < MIN_ALERT_DISTANCE_M) return { show: false, distance, reason: "too-close" };

  // With no heading we cannot tell ahead from behind; show it and let the map speak.
  if (course === null || !Number.isFinite(course)) return { show: true, distance };

  const brg = bearing(me, hazard);
  const off = angDiff(course, brg);
  const cone = speedMps > 12 ? 45 : 75;
  if (off > cone) return { show: false, distance, reason: "behind" };

  const crossTrack = distance * Math.sin(toRad(off));
  if (crossTrack > 150) return { show: false, distance, reason: "off-corridor" };

  if (directional && reporterCourse !== null && Number.isFinite(reporterCourse)) {
    if (angDiff(course, reporterCourse) > 60) {
      return { show: false, distance, reason: "opposite-side" };
    }
  }
  return { show: true, distance };
}

/** Seconds until the driver reaches the hazard. Alerts fire on time, not distance. */
export function timeToHazard(distanceM: number, speedMps: number): number {
  if (speedMps <= 0.5) return Infinity;
  return distanceM / speedMps;
}

/** Point reached by travelling `distanceM` from `from` on the given bearing. */
export function destination(from: Point, bearingDeg: number, distanceM: number): Point {
  const d = distanceM / R;
  const brg = toRad(bearingDeg);
  const la1 = toRad(from.lat);
  const lo1 = toRad(from.lon);
  const la2 = Math.asin(Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(brg));
  const lo2 =
    lo1 +
    Math.atan2(Math.sin(brg) * Math.sin(d) * Math.cos(la1), Math.cos(d) - Math.sin(la1) * Math.sin(la2));
  return { lat: toDeg(la2), lon: ((toDeg(lo2) + 540) % 360) - 180 };
}
